import { HttpClient, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { Permit, PermitSearchSpec, CountyInspector, InspectorListSearchCriteria, RegeneratePermitRequest, RegeneratedPermitResult, LandownerProgramStatusResponse, LandownerPassthroughProgramApplication, LandownerProgramApplicationResponse, LandownerProgramSearchSpec, LandownerProgramQueryResult, LandownerProgramCompleteWellRequest, LandownerProgramCompleteWellResponse, LandownerPassthroughProgramDecision, LandownerPassthroughProgramNote } from "../models/permit.model";
import { Signature } from "../models/flow.dto";

@Injectable()

export class PermitService {
    static BaseUrl: string = "/api/permit";

    static InspectionFlowTemplateIds = ['nUYL1gZnUZ4T9m2rm', 'oHGriJaoHGrZ5sHF9'];
    static PermitFlowTemplateIds = ['nxc~8T~nxc~fpJ0cFz', 'nxJe7mqnxOxhi~2Noi', 'nAAWDxInAAWFRK1EQA', 'nABkOa5nABkQ~I25Nm'];

    constructor(public http: HttpClient) { 
    }

    checkLandownerProgramStatus(api: string): Observable<LandownerProgramStatusResponse[]> {
        return this.http.get<LandownerProgramStatusResponse[]>(`${PermitService.BaseUrl}/check-landowner-program-status/${api}`);
    }

    getOrganizationPermits(orgId: string): Observable<Permit[]> {
        return this.http.get<Permit[]>(`${PermitService.BaseUrl}/org/${orgId}`);
    }

    getWellPermits(wellId: string): Observable<Permit[]> {
        return this.http.get<Permit[]>(`${PermitService.BaseUrl}/well/${wellId}`);
    }

    getPermitSignature(permitId: string): Observable<Signature> {
        return this.http.get<Signature>(`/api/flow/permit-signature/${permitId}`);
    }

    getPermit(id: string, includeSchematic: boolean): Observable<Permit> {
        return this.http.get<any>(`${PermitService.BaseUrl}/get/${id}?includeSchematic=${includeSchematic}`);
    }

    getPermitPdf(id: string): Observable<any> {
        return this.http.get(`${PermitService.BaseUrl}/getPdf/${id}`);
    }

    regeneratePermit(req: RegeneratePermitRequest): Observable<RegeneratedPermitResult> {
        return this.http.post<RegeneratedPermitResult>(`${PermitService.BaseUrl}/regenerate`, req);
    }

    saveLandownerApplication(application: LandownerPassthroughProgramApplication): Observable<LandownerProgramApplicationResponse> {
        return this.http.post<LandownerProgramApplicationResponse>('/api/permit/save-landowner-application', application);
    }

    searchInspectorList(criteria: InspectorListSearchCriteria): Observable<CountyInspector[]> {
        const query = this._buildQueryString(criteria);
        return this.http.get<CountyInspector[]>(`/api/permit/inspectorList?${query}`);
    }

    completeLandownerPassthroughApplication(request: LandownerProgramCompleteWellRequest): Observable<LandownerProgramCompleteWellResponse> {
        return this.http.post<LandownerProgramCompleteWellResponse>("/api/permit/update-landowner-program-status", request);
    }

    findLandownerPassthroughApplicationById(applicationId: string): Observable<LandownerPassthroughProgramApplication> {
        const spec = {
            applicationId: applicationId,
            ensureInspectors: true
        } as LandownerProgramSearchSpec;
        return this.searchLandownerPassthroughApplications(spec).pipe(
            map((data: LandownerPassthroughProgramApplication[]) => {
                if (data == null || data.length == 0) return null;
                return data[0];
            })
        );
    }

    addLandownerApplicationNote(note: LandownerPassthroughProgramNote): Observable<LandownerPassthroughProgramNote> {
        return this.http.post<LandownerPassthroughProgramNote>('/api/permit/add-landowner-application-note', note);
    }

    findPermits(spec: PermitSearchSpec): Observable<Permit[]> {
        return this.http.post<Permit[]>(`${PermitService.BaseUrl}/find-permits`, spec);
    }

    searchLandownerPassthroughApplications(spec: LandownerProgramSearchSpec): Observable<LandownerProgramQueryResult[]> {
        return this.http.post<LandownerProgramQueryResult[]>('/api/permit/search-passthrough-applications', spec);
    }

    submitLandownerPassthroughApplication(application: LandownerPassthroughProgramApplication): Observable<LandownerProgramApplicationResponse> {
        return this.http.post<LandownerProgramApplicationResponse>('/api/permit/submit-passthrough-application', application);
    }

    decisionLandownerPassthroughApplication(decision: LandownerPassthroughProgramDecision): Observable<LandownerProgramApplicationResponse> {
        return this.http.post<LandownerProgramApplicationResponse>('/api/permit/decision-passthrough-application', decision);
    }

    updateInspectors(modified: CountyInspector[]): Observable<CountyInspector[]> {
        return this.http.post<CountyInspector[]>("/api/permit/update-inspectors", modified);
    }

    uploadRegeneratedPermit(data: RegeneratedPermitResult): Observable<RegeneratedPermitResult> {
        return this.http.post<RegeneratedPermitResult>("/api/permit/upload-regenerated-permit", data);
    }

    public _buildQueryString(criteria: InspectorListSearchCriteria): string {
        const params = new URLSearchParams;
        for (let key in criteria) {
            if (criteria.hasOwnProperty(key)) {
                const value = criteria[key];
                if (value instanceof Array) {
                    for (let index in value) {
                        if (value.hasOwnProperty(index)) {
                            params.set(key + "[" + index + "]", value[index]);
                        }
                    }
                } else if (value instanceof Date) {
                    params.set(key, value.toISOString());
                } else {
                    params.set(key, value.toString());
                }
            }
        }
        return params.toString();
    }
}
